import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { Lightbox } from "@/components/Lightbox";
import { GridPreloader } from "@/components/GridPreloader";
import { SelectionOnboarding } from "@/components/SelectionOnboarding";
import { GalleryHeader } from "@/components/gallery/GalleryHeader";
import { PhotoGrid } from "@/components/gallery/PhotoGrid";
import { NotesStep } from "@/components/gallery/NotesStep";
import { PrintStep } from "@/components/gallery/PrintStep";
import { SelectionBar } from "@/components/gallery/SelectionBar";
import { TrialUpsellModal } from "@/components/gallery/TrialUpsellModal";
import { logError } from "@/lib/log";

// Three steps, down from five: choose (base + extra in one grid), annotate, route prints.
const STEPS = ["step_select", "step_notes", "step_prints"];

let rowSeq = 0;
const newRowId = () => `row_${++rowSeq}`;

export default function ClientGallery() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(0);
  const [edits, setEdits] = useState({});
  const [extras, setExtras] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [prints, setPrints] = useState([]);
  const [generalNote, setGeneralNote] = useState("");
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [cacheReady, setCacheReady] = useState(false);
  // Stable so the preloader's image-caching effect never restarts on a parent render.
  const handleCacheReady = useCallback(() => setCacheReady(true), []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/gallery/${token}`);
        if (res.data.status === "submitted") {
          navigate(`/g/${token}/summary`, { replace: true });
          return;
        }
        setData(res.data);
        setEdits(res.data.edits || {});
        setExtras(res.data.extra_edits || []);
        setRejected(res.data.rejected || []);
        setPrints((res.data.prints || []).map((p) => ({ ...p, qty: p.qty || 1, row_id: newRowId() })));
        setGeneralNote(res.data.general_note || "");
        if (!localStorage.getItem(`seleksa_ob_${token}`)) setShowOnboarding(true);
      } catch (err) {
        logError("Could not load gallery", err);
        setNotFound(true);
      }
    })();
  }, [token, navigate]);

  const photoById = useMemo(() => Object.fromEntries((data?.photos || []).map((p) => [p.file_id, p])), [data]);
  const editIds = Object.keys(edits);
  const baseIds = editIds.filter((id) => !extras.includes(id));
  // "Edit only" galleries have nothing to print, so they finish in two steps.
  const steps = useMemo(
    () => (data?.print_enabled === false ? STEPS.filter((s) => s !== "step_prints") : STEPS),
    [data]
  );
  const cur = steps[Math.min(step, steps.length - 1)];
  const isLastStep = step === steps.length - 1;

  const includedQty = useMemo(() => {
    const map = {};
    (data?.print_quota_items || []).forEach((i) => (map[i.size] = Number(i.qty || 0)));
    return map;
  }, [data]);

  // How many of each pre-paid print size are still unused, for the live counters.
  const remainingQty = useMemo(() => {
    const used = {};
    prints.forEach((p) => (used[p.size] = (used[p.size] || 0) + Number(p.qty || 1)));
    return Object.fromEntries(
      Object.entries(includedQty).map(([size, qty]) => [size, Math.max(0, qty - (used[size] || 0))])
    );
  }, [includedQty, prints]);

  const extraCost = useMemo(() => {
    if (!data) return 0;
    let total = extras.length * (data.pricing?.extra_edit_price || 0);
    const counts = {};
    prints.forEach((p) => (counts[p.size] = (counts[p.size] || 0) + Number(p.qty || 1)));
    Object.entries(counts).forEach(([size, count]) => {
      const extraCount = Math.max(0, count - (includedQty[size] || 0));
      total += extraCount * (data.pricing?.print_prices?.[size] || 0);
    });
    total += prints.filter((p) => p.frame_code).reduce((a, p) => a + Number(p.qty || 1), 0)
      * (data.pricing?.frame_price || 0);
    return total;
  }, [data, extras, prints, includedQty]);

  const extraPrice = data?.pricing?.extra_edit_price || 0;
  const maxTotal = (data?.edit_quota || 0) + (data?.allow_extra_edits ? (data?.max_extra_edits || 0) : 0);

  /**
   * One grid for everything: the first `edit_quota` picks are included, and anything
   * beyond that is automatically an extra — announced once, the moment it happens.
   */
  const keepPhoto = (fileId) => {
    if (fileId in edits) return;
    const quota = data.edit_quota;
    const isExtra = baseIds.length >= quota;

    if (isExtra && !data.allow_extra_edits) {
      toast.error(t("quota_full_no_extra", { n: quota }));
      return;
    }
    if (isExtra && extras.length >= (data.max_extra_edits || 0)) {
      toast.error(t("extra_cap_reached", { n: data.max_extra_edits }));
      return;
    }
    if (isExtra) {
      setExtras((e) => (e.includes(fileId) ? e : [...e, fileId]));
      // Announce the crossing once, on the first chargeable pick.
      if (extras.length === 0) toast.warning(t("quota_reached_toast", { n: quota, price: IDR(extraPrice) }));
      else toast.info(t("extra_added_toast", { price: IDR(extraPrice) }));
    }
    setEdits((prev) => (fileId in prev ? prev : { ...prev, [fileId]: "" }));
    setRejected((r) => r.filter((x) => x !== fileId));
  };

  const dropPhoto = (fileId, skipReject = false) => {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    setExtras((e) => e.filter((x) => x !== fileId));
    setPrints((p) => p.filter((x) => x.file_id !== fileId));
    if (!skipReject) setRejected((r) => (r.includes(fileId) ? r : [...r, fileId]));
    else setRejected((r) => r.filter((x) => x !== fileId));
  };

  const toggleSelect = (fileId) => {
    if (fileId in edits) dropPhoto(fileId);
    else keepPhoto(fileId);
  };

  /** Bulk helpers: fill only the pre-paid quota, never silently add chargeable extras. */
  const selectRemaining = () => {
    const slots = data.edit_quota - baseIds.length;
    if (slots <= 0) {
      toast.info(t("quota_used_up"));
      return;
    }
    const picks = data.photos.filter((p) => !(p.file_id in edits)).slice(0, slots).map((p) => p.file_id);
    if (picks.length === 0) return;
    setEdits((prev) => ({ ...prev, ...Object.fromEntries(picks.map((id) => [id, ""])) }));
    setRejected((r) => r.filter((x) => !picks.includes(x)));
    toast.success(t("bulk_selected", { n: picks.length }));
  };

  const unselectAll = () => {
    setEdits({});
    setExtras([]);
    setPrints([]);
    setRejected([]);
  };

  const setNote = (fileId, value) => setEdits((prev) => ({ ...prev, [fileId]: value }));

  /** Prefer a size the client has already paid for. */
  const defaultSize = () =>
    Object.keys(remainingQty).find((s) => remainingQty[s] > 0) || data.print_sizes[0] || "";

  const addPrint = (fileId, size) =>
    setPrints((prev) => [
      ...prev,
      { row_id: newRowId(), file_id: fileId, size: size || defaultSize(), qty: 1, frame_code: "", note: "" },
    ]);

  const removePrint = (rowId) => setPrints((prev) => prev.filter((p) => p.row_id !== rowId));

  const updatePrint = (rowId, key, value) =>
    setPrints((prev) => prev.map((p) => (p.row_id === rowId ? { ...p, [key]: value } : p)));

  const payload = () => ({
    edits,
    extra_edits: extras,
    rejected,
    prints: prints.map(({ row_id, ...rest }) => ({ ...rest, qty: Math.max(1, Number(rest.qty) || 1) })),
    general_note: generalNote,
  });

  const saveDraft = async (silent = false) => {
    setBusy(true);
    try {
      await api.put(`/gallery/${token}/selection`, payload());
      if (!silent) toast.success(t("save"));
      return true;
    } catch (err) {
      toast.error(apiError(err, "Could not save"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (data?.watermark && !showUpsell && !sessionStorage.getItem(`seleksa_upsell_${token}`)) {
      sessionStorage.setItem(`seleksa_upsell_${token}`, "1");
      setShowUpsell(true);
      return;
    }
    setShowUpsell(false);
    setBusy(true);
    try {
      await api.post(`/gallery/${token}/submit`, payload());
      toast.success(t("submitted_ok"));
      navigate(`/g/${token}/summary`, { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Could not submit"));
    } finally {
      setBusy(false);
    }
  };

  // Instant next/prev: warm the neighbouring full-size images while one is open.
  useEffect(() => {
    if (!zoomPhoto || !data) return;
    const list = data.photos;
    const i = list.findIndex((p) => p.file_id === zoomPhoto.file_id);
    if (i < 0) return;
    [-2, -1, 1, 2].forEach((offset) => {
      const neighbour = list[(i + offset + list.length) % list.length];
      if (!neighbour || neighbour.file_id === zoomPhoto.file_id) return;
      const img = new Image();
      img.decoding = "async";
      img.src = neighbour.preview || neighbour.thumb;
    });
  }, [zoomPhoto, data]);

  if (notFound)
    return (
      <div className="min-h-screen bg-[#050505] text-[#F3F3F1] flex items-center justify-center px-6" data-testid="gallery-not-found">
        <h1 className="text-3xl tracking-tighter text-center">This link is no longer valid.</h1>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#707070]">
        <Loader2 className="animate-spin" />
      </div>
    );

  const selectedPhotos = editIds.map((id) => photoById[id]).filter(Boolean);

  if (!cacheReady)
    return <GridPreloader photos={data.photos} onReady={handleCacheReady} />;

  const step1Photo = (fileId) => ({
    selected: fileId in edits,
    isExtra: extras.includes(fileId),
  });

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain pb-40" data-testid="client-gallery-page">
      <GalleryHeader data={data} steps={steps} step={step} cur={cur} />

      <main className="max-w-7xl mx-auto px-5 lg:px-12 py-10 lg:py-14">
        {cur === "step_select" && (
          <PhotoGrid data={data} edits={edits} extras={extras} baseCount={baseIds.length}
            maxTotal={maxTotal} extraPrice={extraPrice}
            onZoom={setZoomPhoto} onToggleSelect={toggleSelect}
            onSelectRemaining={selectRemaining} onUnselectAll={unselectAll}
            onShowHelp={() => setShowOnboarding(true)} />
        )}

        {cur === "step_notes" && (
          <NotesStep selectedPhotos={selectedPhotos} edits={edits} extras={extras}
            generalNote={generalNote} onGeneralNote={setGeneralNote}
            onZoom={setZoomPhoto} onDrop={dropPhoto} onNote={setNote} />
        )}

        {cur === "step_prints" && (
          <PrintStep data={data} selectedPhotos={selectedPhotos} prints={prints}
            includedQty={includedQty} remainingQty={remainingQty} extraCost={extraCost}
            onZoom={setZoomPhoto} onAddPrint={addPrint} onRemovePrint={removePrint} onUpdatePrint={updatePrint} />
        )}
      </main>

      {data.watermark && (
        <div className="max-w-7xl mx-auto px-5 lg:px-12 pb-6" data-testid="gallery-watermark">
          <div className="border-t border-[#1E1E1E] pt-5 flex items-center justify-between gap-4">
            <p className="text-[11px] text-[#5A5A5A]">{t("powered_by_body")}</p>
            <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">{t("powered_by")}</span>
          </div>
        </div>
      )}

      <SelectionBar data={data} baseIds={baseIds} extras={extras} prints={prints} extraCost={extraCost}
        step={step} steps={steps} busy={busy} canContinue={editIds.length > 0}
        onSave={() => saveDraft()}
        onBack={() => { setStep(step - 1); window.scrollTo({ top: 0 }); }}
        onNext={async () => {
          if (await saveDraft(true)) {
            setStep(step + 1);
            window.scrollTo({ top: 0 });
          }
        }}
        onSubmit={submit} />

      <Lightbox
        photo={zoomPhoto}
        onClose={() => setZoomPhoto(null)}
        selected={zoomPhoto ? step1Photo(zoomPhoto.file_id).selected : false}
        onToggleSelect={cur === "step_select" ? (fileId) => toggleSelect(fileId) : null}
        onPrev={zoomPhoto && data.photos.length > 1 ? () => {
          const i = data.photos.findIndex((p) => p.file_id === zoomPhoto.file_id);
          setZoomPhoto(data.photos[(i - 1 + data.photos.length) % data.photos.length]);
        } : null}
        onNext={zoomPhoto && data.photos.length > 1 ? () => {
          const i = data.photos.findIndex((p) => p.file_id === zoomPhoto.file_id);
          setZoomPhoto(data.photos[(i + 1) % data.photos.length]);
        } : null}
        selectLabel={baseIds.length >= data.edit_quota
          ? t("add_extra_full", { price: IDR(extraPrice) })
          : t("select_photo")}
        selectedLabel={t("photo_selected")}
      />

      {showUpsell && (
        <TrialUpsellModal busy={busy} onClose={() => setShowUpsell(false)} onSubmit={submit} />
      )}

      {showOnboarding && (
        <SelectionOnboarding onClose={() => {
          localStorage.setItem(`seleksa_ob_${token}`, "1");
          setShowOnboarding(false);
        }} />
      )}
    </div>
  );
}
